import {
  AccountSettings,
  ActiveSessionsSection,
  aktiviereOrganisation,
  useAuth,
  usePermissions,
} from '@xhubio-saas/core-frontend/kernel'
import { EmailLogTab } from '@xhubio-saas/core-frontend/plugin-email-log'
import { EmailDispatchTab } from '@xhubio-saas/core-frontend/plugin-email-outbox'
import {
  type CredentialListItem,
  GovCredentialsSettingsPage,
  type GovSystemListItem,
  useOrgPassword,
} from '@xhubio-saas/core-frontend/plugin-gov-credentials'
import { LogAuditPage } from '@xhubio-saas/core-frontend/plugin-log-audit'
import { RoleDetailPage, RoleListPage } from '@xhubio-saas/core-frontend/plugin-roles'
import { SecuritySettingsTab } from '@xhubio-saas/core-frontend/plugin-security'
import {
  type AcceptInvitationCallbacks,
  AcceptInvitationPage,
  type AcceptOwnershipTransferCallbacks,
  AcceptOwnershipTransferPage,
  UserDetailPage,
  UserListPage,
  UserProfileForm,
  VerifyEmailChangePage,
} from '@xhubio-saas/core-frontend/plugin-user'
import { toast } from '@xhubio-saas/core-frontend/ui-components'
import { ApiKeysPage } from '@xhubio-saas/frontend-plugin-api-key'
import { PeppolSettingsPage, PeppolTransmissionsPage } from '@xhubio-saas/frontend-plugin-peppol'
import { VerifactuSettingsPage } from '@xhubio-saas/invoice-frontend/plugin-invoice'
import {
  extractAllowedCountries,
  type FormatDetectionOutcome,
  useCountryFormatCatalog,
  useInvoiceConversion,
  useInvoiceRestClient,
  useRestApiCall,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { LogInvoicePage } from '@xhubio-saas/invoice-frontend/plugin-log-invoice'
import type { BlockTemplate } from '@xhubio-saas/pdf-editor-frontend'
import { type Template, TemplateListPage } from '@xhubio-saas/pdf-frontend/plugin-pdf'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { trpc } from '@/trpc'
import { useApiKeyClient } from './api/apiKeyClient'
import { useEmailLogClient } from './api/emailLogClient'
import { useGovCredentialsClient } from './api/govCredentialsClient'
import { useInvoiceClient } from './api/invoiceClient'
import { parseAndValidate, type ValidationResult } from './api/invoiceValidationClient'
import { useBuyerCountry } from './api/organisationsLand'
import { createPdfExtractClient, type ExtractOutcome } from './api/pdfExtractClient'
import { starterTemplate, usePdfTemplateClient } from './api/pdfTemplateClient'
import {
  usePeppolRegistration,
  usePeppolSaveSettings,
  usePeppolSettings,
  usePeppolTransmissions,
} from './api/peppolClient'
import { useRoleClient } from './api/roleClient'
import { useSavedPdfTemplates } from './api/savedPdfTemplates'
import {
  type SellerProfile,
  useSaveSellerProfile,
  useSellerProfile,
} from './api/sellerProfileClient'
import { useSubscriptionClient } from './api/subscriptionClient'
import { useTwoFactorClient } from './api/twoFactorClient'
import { useUsageClient } from './api/usageClient'
import { useUserClient } from './api/userClient'
import { useVerifactuClient } from './api/verifactuClient'
import { ConsentGuard } from './components/ConsentGuard'
import { EntitlementRestrictionContext } from './components/EntitlementRestrictionContext'
import { OwnershipTransferSection } from './components/OwnershipTransferSection'
import {
  useCanInviteMembers,
  useCanManageMemberStatus,
  useCanManageRoles,
  useCanManageSellerProfile,
} from './hooks/useMemberAdminRights'
import { sitzungsschluesselZustand, useTempApiKey } from './hooks/useTempApiKey'
import { SettingsLayout } from './layouts/SettingsLayout'
import { useOrgMeasurementSystem } from './lib/useOrgLocale'
import { zurAnmeldung } from './lib/zur-anmeldung'
import { BillingPage } from './pages/protected/BillingPage'
import { localeOf } from './pages/protected/billing/billingState'
import { ChangelogPage } from './pages/protected/ChangelogPage'
import { InvoiceConversionPage } from './pages/protected/InvoiceConversionPage'
import { InvoiceFromPdfPage } from './pages/protected/InvoiceFromPdfPage'
import { InvoiceValidationPage } from './pages/protected/InvoiceValidationPage'
import { SellerProfilePage } from './pages/protected/SellerProfilePage'
import {
  CookieSettingsPage,
  LegalSettingsPage,
  SettingsEmailPage,
  SettingsNotificationsPage,
  SettingsSystemPage,
} from './pages/protected/settings'
import { saasConfig } from './saas.config'

// ═══════════════════════════════════════════════════════════════════════════
// Page Wrapper Components
// ═══════════════════════════════════════════════════════════════════════════

/**
 * API Keys Page with real backend connection
 */
export function ApiKeysPageWithProviders() {
  const { t, i18n } = useTranslation('apiKey')
  const apiClient = useApiKeyClient()
  // ApiKeysPage (frontend-plugin-api-key) erwartet t(key, params?) mit
  // Interpolations-Werten — exakt auf i18next spiegeln (Vorbild dashandwerk).
  // Die fruehere ambiente Deklaration in src/types/ erfand den Vertrag neu
  // (fallback: string) und machte den Typcheck blind; das publizierte Paket
  // liefert laengst echte Typen.
  const translate = useCallback(
    (key: string, params?: Record<string, unknown>): string => t(key, params),
    [t]
  )
  return (
    <ConsentGuard>
      <ApiKeysPage apiClient={apiClient} t={translate} locale={i18n.language} />
    </ConsentGuard>
  )
}

/**
 * Peppol settings page with real backend connection — defaults to
 * sandbox; production tab can be added once the kernel-plugin exposes
 * a tab switch in the page itself.
 */
export function PeppolSettingsPageWithProviders() {
  // Customers see the ActivationPanel (toggle + own Peppol id + status);
  // the operator/BYO-AP form (provider, apiKeyRef, mock) only renders
  // when the deployment sets VITE_PEPPOL_SHOW_ADVANCED=true — an
  // entitlement-based gate (peppol:settings:admin) is a planned
  // follow-up of plan-peppol-customer-onboarding.md.
  const { t } = useTranslation('peppol')
  const { data: settings } = usePeppolSettings('sandbox')
  const onSave = usePeppolSaveSettings()
  const { status, isLoading, activate, deactivate } = usePeppolRegistration('sandbox')
  const translate = useCallback((key: string) => t(key), [t])
  const showAdvanced = import.meta.env.VITE_PEPPOL_SHOW_ADVANCED === 'true'
  return (
    <ConsentGuard>
      <PeppolSettingsPage
        settings={settings ?? null}
        environment="sandbox"
        t={translate}
        onSave={onSave}
        status={status}
        loading={isLoading}
        onActivate={activate}
        onDeactivate={deactivate}
        showAdvanced={showAdvanced}
        allowMock={showAdvanced}
      />
    </ConsentGuard>
  )
}

/**
 * Peppol transmissions log with real backend connection.
 */
export function PeppolTransmissionsPageWithProviders() {
  const { t } = useTranslation('peppol')
  const { rows } = usePeppolTransmissions()
  const translate = useCallback((key: string) => t(key), [t])
  return (
    <ConsentGuard>
      <PeppolTransmissionsPage rows={rows} t={translate} />
    </ConsentGuard>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Users / Team (plugin-user)
// ═══════════════════════════════════════════════════════════════════════════

export function UserListPageWithProviders() {
  const { t } = useTranslation('user')
  const navigate = useNavigate()
  const apiClient = useUserClient()
  // Ohne diese Werte fallen `canInvite`/`canManageStatus` auf ihren Default
  // `false` — Einladen und Aktivieren/Deaktivieren waeren dann fuer ALLE
  // unsichtbar, auch fuer den Owner.
  const canInvite = useCanInviteMembers()
  const canManageStatus = useCanManageMemberStatus()
  return (
    <ConsentGuard>
      <UserListPage
        apiClient={apiClient}
        t={t}
        canInvite={canInvite}
        canManageStatus={canManageStatus}
        onViewDetail={(member) => navigate(`/users/${member.id}`)}
        // 🔴 `onEdit` fuehrt auf das **Profilformular**, `onViewDetail` auf die
        // Detailseite. Die beiden sind seit `core-frontend 4.0.0` nicht mehr
        // dasselbe: die Detailseite zeigt `UserForm` (Stammdaten, Status), das
        // Profilformular `UserProfileForm` — und nur dort steht „E-Mail
        // aendern". Bis 2026-09-21 zeigten beide auf die Detailseite, und damit
        // war der Adresswechsel aus der Oberflaeche heraus unerreichbar.
        onEdit={(member) => navigate(`/users/${member.id}/edit`)}
      />
      {/* Eigentuemerwechsel. `UserListPage` fuehrt keinen Slot fuer eigene
          Aktionen, deshalb haengt die Sektion unter der Liste — sichtbar nur
          fuer den Eigentuemer. */}
      <OwnershipTransferSection />
    </ConsentGuard>
  )
}

export function UserDetailPageWithProviders() {
  const { t } = useTranslation('user')
  // 🔴 `:userId`, nicht `:memberId`. Was hier durchlaeuft, ist die **Nutzer-Id**:
  // `members.list` liefert `id: user.id`, und `getMember(x)` ruft
  // `members.get({ userId: x })`. Der frueher hier stehende Name `memberId`
  // versprach eine Mitglieds-Id, die es an dieser Stelle nie gab.
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  // 🔴 core-frontend 4.0.0: die Detailseite ZEIGT das Formular immer und
  // speichert selbst — es gibt keine separate `/users/:id/edit`-Route mehr,
  // die den Client mit der Ziel-Id gebunden haette. Ohne `userId` hier bindet
  // `useUserClient()` an „ich selbst"; ein Owner, der einen Kollegen
  // bearbeitet, wuerde damit wieder still sein EIGENES Profil ueberschreiben
  // (derselbe Defekt wie Plan API 2026-08-20/21 und 2026-08-21/05, nur jetzt
  // auf dieser Seite statt auf der abgebauten Edit-Route).
  const apiClient = useUserClient(userId)
  const canManageStatus = useCanManageMemberStatus()
  return (
    <ConsentGuard>
      <UserDetailPage
        apiClient={apiClient}
        // Der Prop-Name gehoert core-frontend und bleibt `memberId`; der Wert
        // ist die Nutzer-Id — genau die, die der Client an `members.get` gibt.
        memberId={userId ?? ''}
        t={t}
        canManageStatus={canManageStatus}
        onBack={() => navigate('/users')}
      />
    </ConsentGuard>
  )
}

/**
 * Das Profilformular — und der **einzige** Ort, an dem eine E-Mail-Adresse
 * gewechselt werden kann.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * \u{1F534} Warum diese Route wieder existiert
 * ─────────────────────────────────────────────────────────────────────────────
 * Mit `core-frontend 4.0.0` (`516d403`, 2026-09-06) fiel `/users/:id/edit`, weil
 * die Detailseite seither sofort im Bearbeiten-Modus steht. Übersehen wurde
 * dabei, dass der Knopf „E-Mail ändern" (`users-profile-change-email`) samt
 * {@link ChangeEmailDialog} **ausschliesslich** in `UserProfileForm` haengt —
 * und die rendert `UserDetailPage` nicht. Gemessen am 2026-09-21: **keine** der
 * vier Apps rendert sie (invoice-api 0, dashandwerk 0, verein 0,
 * hausverwaltung 0). Damit konnte fuenfzehn Tage lang **niemand** seine
 * Anmeldeadresse aendern — gebaut war alles, erreichbar nichts.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * \u{1F534} Warum die Id in den Client MUSS
 * ─────────────────────────────────────────────────────────────────────────────
 * `useUserClient(userId)` haengt `targetUserId` an `members.updateProfile`
 * (`api/userClient.ts:206`) **und** an `members.requestEmailChange` (`:289`).
 * Ohne die Id loest der Server die betroffene Person aus `session.userId` auf:
 * ein Eigentuemer, der die Adresse eines Kollegen pflegt, bekaeme BEIDE
 * Bestaetigungsmails selbst und bestaetigte im schlechtesten Fall unbemerkt den
 * Wechsel SEINER eigenen Anmeldeadresse. Genau dieser Defekt ist schon zweimal
 * dagewesen (Plaene API 2026-08-20/21 und 2026-08-21/05); der Server kann ihn
 * seit `requestEmailChange`s `targetUserId` nicht mehr machen, **wenn** die
 * Seite die Id reicht. Bewacht von `ApiMailKonto` E_3.
 *
 * \u{26AA} `currentUserId` ist die Id des **Angemeldeten**, nicht die des
 * Bearbeiteten: nur damit kann die Karte zwischen Eigenprofil- und Fremdsicht
 * unterscheiden. Fehlt sie, spricht sie immer in der Eigenprofil-Sicht — das
 * waere fuer den Kollegen-Fall der falsche Text.
 */
export function UserProfileEditPageWithProviders() {
  const { t } = useTranslation('user')
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const apiClient = useUserClient(userId)
  const zurueck = () => navigate(`/users/${userId ?? ''}`)
  return (
    <ConsentGuard>
      <UserProfileForm
        apiClient={apiClient}
        memberId={userId ?? ''}
        currentUserId={user?.id}
        t={t}
        onCancel={zurueck}
        onSaved={zurueck}
      />
    </ConsentGuard>
  )
}

export function AcceptInvitationPageWithProviders() {
  const { t } = useTranslation('user')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, session } = useAuth()
  const apiClient = useUserClient()

  const token = searchParams.get('token') ?? ''

  const callbacks: AcceptInvitationCallbacks = {
    isLoggedIn: () => !!session,
    getCurrentUserEmail: () => user?.email ?? null,
    navigateToRegister: (tkn: string, email: string) => {
      navigate(`/register?invitation=${tkn}&email=${encodeURIComponent(email)}`)
    },
    navigateToLogin: (redirectUrl: string) => {
      zurAnmeldung(navigate, redirectUrl)
    },
    setActiveOrganization: async (organizationId: string) => {
      // Plan CORE 09-26/04 S2: der Helfer des Kerns wirft bei einer Absage
      // (`OrganizationSwitchError`), statt `{ error }` zu verschlucken.
      await aktiviereOrganisation(organizationId)
      await new Promise((resolve) => setTimeout(resolve, 100))
    },
    navigateToDashboard: () => {
      window.location.href = '/billing'
    },
  }

  return <AcceptInvitationPage apiClient={apiClient} callbacks={callbacks} token={token} t={t} />
}

/**
 * Verify Email Change Page (public)
 */
export function VerifyEmailChangePageWithProviders() {
  const { t } = useTranslation('user')
  const [searchParams] = useSearchParams()
  const apiClient = useUserClient()
  return (
    <VerifyEmailChangePage
      apiClient={apiClient}
      searchParams={{
        token: searchParams.get('token') ?? undefined,
        action: searchParams.get('action') ?? undefined,
      }}
      onBackToLogin={() => {
        window.location.href = '/login'
      }}
      t={t}
    />
  )
}

/**
 * Accept Ownership Transfer Page (public)
 */
export function AcceptOwnershipTransferPageWithProviders() {
  const { t } = useTranslation('user')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { session } = useAuth()
  const apiClient = useUserClient()

  const callbacks: AcceptOwnershipTransferCallbacks = {
    isLoggedIn: () => !!session,
    navigateToLogin: (redirectUrl: string) => {
      zurAnmeldung(navigate, redirectUrl)
    },
    navigateToDashboard: () => {
      window.location.href = '/billing'
    },
    setActiveOrganization: async (organizationId: string) => {
      // Plan CORE 09-26/04 S2: der Helfer des Kerns wirft bei einer Absage
      // (`OrganizationSwitchError`), statt `{ error }` zu verschlucken.
      await aktiviereOrganisation(organizationId)
      await new Promise((resolve) => setTimeout(resolve, 100))
    },
  }

  return (
    <AcceptOwnershipTransferPage
      apiClient={apiClient}
      callbacks={callbacks}
      searchParams={{
        token: searchParams.get('token') ?? undefined,
        action: searchParams.get('action') ?? undefined,
      }}
      t={t}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Roles (plugin-roles)
// ═══════════════════════════════════════════════════════════════════════════

export function RoleListPageWithProviders() {
  const { t } = useTranslation('roles')
  const navigate = useNavigate()
  const apiClient = useRoleClient()
  const canManageRoles = useCanManageRoles()
  return (
    <ConsentGuard>
      <RoleListPage
        apiClient={apiClient}
        t={t}
        canManageRoles={canManageRoles}
        onNavigateToDetail={(roleId) => navigate(`/settings/roles/${roleId}`)}
      />
    </ConsentGuard>
  )
}

export function RoleDetailPageWithProviders() {
  const { t } = useTranslation('roles')
  const { roleId } = useParams<{ roleId: string }>()
  const navigate = useNavigate()
  const apiClient = useRoleClient()
  return (
    <ConsentGuard>
      <RoleDetailPage
        apiClient={apiClient}
        roleId={roleId ?? ''}
        t={t}
        onBack={() => navigate('/settings/roles')}
      />
    </ConsentGuard>
  )
}

/**
 * Invoice Conversion Page with REST API backend connection.
 * Uses the actual REST API (same as external consumers) instead of tRPC,
 * and shows an API Request Inspector for developer documentation.
 */
export function InvoiceConversionPageWithProviders() {
  const { billingEntitlements } = useContext(EntitlementRestrictionContext)

  // Temp API key for REST calls (session-bound)
  const { apiKey, maskedApiKey, isLoading: keyLoading } = useTempApiKey()

  // REST API call state (captures request/response for inspector)
  const restApiCall = useRestApiCall()

  // REST-based client (replaces tRPC for conversion calls)
  // Use configured API URL for production; fall back to origin for local dev (Vite proxy)
  const restBaseUrl = useMemo(() => saasConfig.api.baseUrl || window.location.origin, [])
  const restClient = useInvoiceRestClient({
    apiKey,
    baseUrl: restBaseUrl,
    restApiCall,
  })

  // 🔴 Eine Tür (KW-003, Entscheid observer 10-09 (a)): konvertiert wird NUR ueber
  // die REST-Route mit Sitzungsschluessel. Der fruehere tRPC-Rueckfall lief genau
  // dann, wenn der Schluessel noch lud oder fehlte, und kannte AT/FR/ES/CZ:ubl
  // nicht (Issue API 2026-10-08, M2b). Ohne Schluessel sperrt die Karte den Knopf
  // und nennt den Grund (`sitzungsschluessel` unten).
  //
  // Die Quellformat-Erkennung laeuft weiter ueber tRPC (F3) — die REST-Route hat
  // keinen Erkennungs-Endpunkt. Ohne diese Zusammensetzung fehlte der Karte der
  // Eintrag „Automatisch erkennen".
  const tRPCClient = useInvoiceClient()
  const apiClient = useMemo(
    () => ({
      ...restClient,
      detectFormat: tRPCClient.detectFormat,
    }),
    [restClient, tRPCClient]
  )

  // Load saved templates for the template selector
  const savedTemplates = useSavedPdfTemplates(restBaseUrl)

  // Seit dem Redesign 2026-10-10 rendert hier die App-Seite (Single Column Report) statt der Paket-Seite. Die Hooks
  // des Pakets bleiben: Katalog und Aufruf laufen wie zuvor ueber den zusammengesetzten Client.
  const {
    catalog,
    isLoading: catalogLoading,
    error: catalogError,
    fetchCatalog,
  } = useCountryFormatCatalog(apiClient)
  const { convert } = useInvoiceConversion(apiClient)

  useEffect(() => {
    // Neu laden, wenn der Client wechselt (der Schluessel kam) — ohne Schluessel ist der Katalog leer.
    void fetchCatalog()
  }, [fetchCatalog])

  /** Erkennung nur, wenn der Client sie hat — sonst bietet die Seite „Automatisch erkennen" nicht an. */
  const onDetectFormat = useMemo(() => {
    const detect = apiClient.detectFormat
    if (!detect) return undefined
    return (
      content: string,
      countryCode: string,
      mimeType?: 'application/pdf'
    ): Promise<FormatDetectionOutcome> =>
      detect.call(apiClient, { content, countryCode, ...(mimeType && { mimeType }) })
  }, [apiClient])

  return (
    <ConsentGuard>
      {/* Ohne Sitzungsschluessel nennt die Auftragskarte den Grund (sessionKey.loading /
          sessionKey.unavailable) — keine zweite Notiz hier. Der fruehere Hinweisbanner ist der Untertitel. */}
      <InvoiceConversionPage
        catalog={catalog}
        catalogLoading={catalogLoading}
        catalogError={catalogError}
        onConvert={convert}
        onDetectFormat={onDetectFormat}
        billingEntitlements={billingEntitlements}
        requestState={apiKey ? restApiCall.state : undefined}
        apiKey={apiKey}
        maskedApiKey={maskedApiKey}
        onResetRequestState={restApiCall.reset}
        savedTemplates={savedTemplates}
        sitzungsschluessel={sitzungsschluesselZustand({ apiKey, isLoading: keyLoading })}
      />
    </ConsentGuard>
  )
}

/** Der Quota-Typ „PDF auslesen" (Plan API 2026-11-01/01, E5) in der Form der Billing-Abfragen (`quota:<id>`). */
const PDF_EXTRACT_QUOTA = 'quota:pdf_extractions'

/** Das Recht, das ein Tarif fuer „E-Rechnung aus PDF" tragen muss (E7) — Free fuehrt es nicht. */
const PDF_EXTRACT_ENTITLEMENT = 'pdf:invoice:extract'

/**
 * E-Rechnung aus PDF — das eigene PDF auslesen, pruefen, die E-Rechnung anheften (Plan API 2026-11-01/01, Schritt 4).
 *
 * Wie die Konvertierung ueber die REST-Tuer mit Sitzungsschluessel (E10). Der Client wird EINMAL gebaut; Schluessel
 * und Erneuern reicht er ueber Refs herein, damit ein neuer Schluessel keinen neuen Client (und keine Render-Schleife)
 * erzeugt. Nach 401/403 holt der Client genau einmal einen neuen Schluessel und wiederholt (`pdfExtractClient.ts`).
 *
 * Kein `requiredEntitlement` an Menue und Route (E7): Free sieht die Seite mit dem Weg zum Upgrade. Ob der Tarif das
 * Recht traegt, steht in `billingEntitlements` (Vorbild `InvoiceConversionPageWithProviders`).
 */
export function InvoiceFromPdfPageWithProviders() {
  const { billingEntitlements, isLoading: entitlementsLoading } = useContext(
    EntitlementRestrictionContext
  )
  const { apiKey, isLoading: keyLoading, refreshKey } = useTempApiKey()
  const keyRef = useRef(apiKey)
  keyRef.current = apiKey
  const refreshRef = useRef(refreshKey)
  refreshRef.current = refreshKey

  const restBaseUrl = useMemo(() => saasConfig.api.baseUrl || window.location.origin, [])
  const client = useMemo(
    () =>
      createPdfExtractClient({
        baseUrl: restBaseUrl,
        getKey: () => keyRef.current,
        refreshKey: () => refreshRef.current(),
      }),
    [restBaseUrl]
  )
  const savedTemplates = useSavedPdfTemplates(restBaseUrl)
  const seller = useSellerProfile()
  const utils = trpc.useUtils()
  const quotaQuery = trpc.billing.getQuotaStatus.useQuery(
    { eventType: PDF_EXTRACT_QUOTA },
    { retry: false }
  )
  const quotaData = quotaQuery.data
  const quota =
    quotaData?.hasQuota && typeof quotaData.limit === 'number'
      ? {
          used: quotaData.currentUsage,
          limit: quotaData.limit,
        }
      : null

  // Wer schreibt, entwertet: Auslesen und Erzeugen verbrauchen Kontingent — die Zaehler lesen danach frisch.
  const refreshQuota = useCallback(async () => {
    await Promise.all([
      utils.billing.getQuotaStatus.invalidate(),
      utils.billing.getBatchQuotaStatus.invalidate(),
    ])
  }, [utils])

  const onExtract = useCallback(
    async (pdf: string): Promise<ExtractOutcome> => {
      const outcome = await client.extract(pdf)
      await refreshQuota()
      return outcome
    },
    [client, refreshQuota]
  )
  const onCreate = useCallback(
    async (params: Parameters<typeof client.create>[0]) => {
      const outcome = await client.create(params)
      await refreshQuota()
      return outcome
    },
    [client, refreshQuota]
  )

  const access =
    entitlementsLoading && billingEntitlements === null
      ? 'loading'
      : billingEntitlements === null || billingEntitlements.includes(PDF_EXTRACT_ENTITLEMENT)
        ? 'allowed'
        : 'upgrade'

  return (
    <ConsentGuard>
      <InvoiceFromPdfPage
        access={access}
        seller={seller.data ?? null}
        sellerLoading={seller.isLoading}
        quota={quota}
        sitzungsschluessel={sitzungsschluesselZustand({ apiKey, isLoading: keyLoading })}
        onExtract={onExtract}
        onCreate={onCreate}
        savedTemplates={savedTemplates}
      />
    </ConsentGuard>
  )
}

/**
 * Firmendaten — der Absender der E-Rechnungen aus „E-Rechnung aus PDF" (E14/E15).
 *
 * Noch nie gespeichert (`sellerProfile.get` → `null`): Vorbelegung aus den Organisationsdaten (`organization.current`,
 * die Felder von core `BusinessFieldsSchema`). Die Seite wartet auf beide Abfragen, bevor sie das Formular zeigt.
 */
export function SellerProfilePageWithProviders() {
  const query = useSellerProfile()
  const org = trpc.organization.current.useQuery(undefined, { staleTime: 5 * 60 * 1000 })
  const save = useSaveSellerProfile()
  const canEdit = useCanManageSellerProfile()
  const orgData = org.data
  const prefill = useMemo<SellerProfile | null>(() => {
    if (!orgData) return null
    return {
      name: orgData.legalName ?? orgData.name,
      street: orgData.street,
      postalCode: orgData.postalCode,
      city: orgData.city,
      countryCode: orgData.countryCode ?? undefined,
      vatId: orgData.vatId,
      taxId: orgData.taxNumber,
      contact: { phone: orgData.phone, email: orgData.email },
    }
  }, [orgData])
  const { mutateAsync } = save
  const onSave = useCallback((profile: SellerProfile) => mutateAsync(profile), [mutateAsync])

  return (
    <ConsentGuard>
      <SellerProfilePage
        profile={query.data ?? null}
        prefill={prefill}
        loading={query.isLoading || org.isLoading}
        loadError={query.isError ? (query.error?.message ?? '') : null}
        canEdit={canEdit}
        onSave={onSave}
      />
    </ConsentGuard>
  )
}

/**
 * Validation — eine fertige E-Rechnung (XML/PDF) pruefen.
 *
 * ⚪ Spiegelt bewusst die Verdrahtung der Conversion-Seite: derselbe
 * Sitzungsschluessel, derselbe REST-Inspector, derselbe Laenderkatalog. Nur
 * der Aufruf ist app-lokal (`invoiceValidationClient`), weil Validierung eine
 * invoice-api-eigene Flaeche ist — ein Paket-Release fuer eine Seite, die nur
 * diese App hat, waere Umweg (Stand 2026-10-10: Pin = aktuelle 3.52.0).
 */
export function InvoiceValidationPageWithProviders() {
  const { billingEntitlements } = useContext(EntitlementRestrictionContext)
  const { apiKey, maskedApiKey } = useTempApiKey()
  const restApiCall = useRestApiCall()
  const restBaseUrl = useMemo(() => saasConfig.api.baseUrl || window.location.origin, [])
  const restClient = useInvoiceRestClient({
    apiKey,
    baseUrl: restBaseUrl,
    restApiCall,
  })
  const { catalog, fetchCatalog } = useCountryFormatCatalog(restClient)

  useEffect(() => {
    if (apiKey) void fetchCatalog()
  }, [apiKey, fetchCatalog])

  /**
   * Eine Tuer: die REST-Route mit Sitzungsschluessel.
   *
   * 🔴 **Warum keine zweite Tuer ueber tRPC** — am 2026-10-03 gebaut und im
   * Browser wieder ausgebaut. Der tRPC-Router kennt keine Auto-Erkennung;
   * der Weg haette drei Uebersetzungsschichten gebraucht, jede mit Luecken:
   *  1. `detectFormat` war eine QUERY, ging als GET mit dem Input in der URL
   *     (seit Plan API 10-10/09 S4 laeuft die Erkennung als Mutation
   *     `detectDocumentFormat`; die Punkte 2 und 3 gelten weiter).
   *     Eine 5-KB-XRechnung sprengt `maxURLLength` 2048 → „Input is too big
   *     for a single dispatch" (dieselbe Falle wie `usageClient.ts:51-58`).
   *     Nur den Dokumentkopf zu schicken hilft der Erkennung nicht, die das
   *     XML parst.
   *  2. `detectFormat` antwortet im **Parser-Vokabular** (`XRECHNUNG_UBL`,
   *     `UBL_21`, … 27 Werte), `parseStrict` will ein Katalog-Token
   *     (`xrechnung`, … 12 Werte). Dazwischen liegt eine echte
   *     n:m-Beziehung (`CANDIDATE_API_FORMATS` im geteilten Paket).
   *  3. `legacyParseFormat` bildet das Token auf die Alt-Id ab — und liefert
   *     fuer manche Kombinationen `undefined`.
   *
   * Drei Schichten, drei Luecken, fuer einen Randfall: der Schluessel wird
   * automatisch gepraegt und fehlt nur, wenn das Stundenlimit (20 je Nutzer)
   * gerissen ist. Dann sagt die Seite das — ein Satz ist ehrlicher als ein
   * Weg, der bei jedem zweiten Format stillsteht.
   */
  const onValidate = useCallback(
    async (args: {
      fileBase64: string
      fileName: string
      countryCode?: string
      format?: string
    }): Promise<ValidationResult> => {
      if (!apiKey) {
        return {
          status: 401,
          wasRead: false,
          findings: [],
          warnings: [],
          abort: { kind: 'noKey', message: 'no session key', step: 'read' },
        }
      }
      return parseAndValidate({
        execute: restApiCall.execute,
        baseUrl: restBaseUrl,
        apiKey,
        ...args,
        // Der Katalog ist schon geladen — daraus kommt das Token fuer den
        // Pruefpfad, statt aus einer zweiten Liste neben ihm.
        formatsFor: (country) =>
          catalog.find((c) => c.code.toUpperCase() === country.toUpperCase())?.formats ?? [],
      })
    },
    [apiKey, catalog, restApiCall.execute, restBaseUrl]
  )

  return (
    <ConsentGuard>
      <InvoiceValidationPage
        catalog={catalog}
        // 🔴 `toJson` bildet auf die Aktion `parse` ab — und genau darauf
        // wird die Pruefung eines fertigen Dokuments abgerechnet. Nicht
        // `toEInvoice`/`create`: wer fremde Eingangsrechnungen prueft, darf
        // deren Format in aller Regel gar nicht erzeugen. Mit dem alten Gate
        // waere ihm die Handauswahl verschlossen gewesen.
        allowedCountries={extractAllowedCountries(billingEntitlements, 'toJson')}
        requestState={apiKey ? restApiCall.state : undefined}
        apiKey={apiKey}
        maskedApiKey={maskedApiKey}
        onValidate={onValidate}
      />
    </ConsentGuard>
  )
}

/**
 * Billing — one page for plan, status, quota, plans and usage (plan API 2026-10-11/02). Replaces the core
 * `SubscriptionPage` (/subscription) and `UsagePage` (/usage); both addresses redirect here.
 *
 * E7: the page needs `billing:usage:read` (route), changing the plan `billing:subscription:read` — the gate of the
 * former /subscription route. `usePermissions` combines role and billing entitlements like the sidebar does.
 */
export function BillingPageWithProviders() {
  const { i18n } = useTranslation()
  const subscriptionClient = useSubscriptionClient()
  const usageClient = useUsageClient()
  const { hasEntitlement } = usePermissions()
  // The country before a paid checkout (plan API 2026-10-09/02).
  const buyerCountry = useBuyerCountry()
  // Event type names live in the `usage` bundle; their keys contain `:` and `.`, which i18next would read as
  // separators — so straight from the resource bundle (as the former UsagePage wrapper did).
  const usageBundle = i18n.getResourceBundle(i18n.language, 'usage') as
    | { eventTypes?: Record<string, string> }
    | undefined

  return (
    <ConsentGuard>
      <BillingPage
        subscriptionClient={subscriptionClient}
        usageClient={usageClient}
        canManagePlan={hasEntitlement('billing:subscription:read')}
        locale={localeOf(i18n.language)}
        eventTypeLabels={usageBundle?.eventTypes ?? {}}
        buyerCountry={buyerCountry}
      />
    </ConsentGuard>
  )
}

/**
 * PDF Template List Page V2 — host for the new block-native editor
 * alongside the legacy one. Same templates table on the backend,
 * differentiated by `editorVersion` inside the BlockTemplate JSONB.
 *
 * Shows both v1 and v2 rows with badges. All rows are clickable here
 * (the new editor opens v1 too — with a warn-modal — so the user can
 * promote a v1 template to v2). The "+ Neu" action creates a v2
 * template directly.
 */
export function PdfTemplateListV2PageWithProviders() {
  const { t } = useTranslation('pdf')
  const location = useLocation()
  const templateClient = usePdfTemplateClient()
  const trpcUtils = trpc.useUtils()
  const createMutation = trpc.pdf.templateCreate.useMutation()
  const measurementSystem = useOrgMeasurementSystem()

  // Free-form type combobox: instead of a fixed "Invoice" Select we
  // surface every documentType observed in the loaded templates plus
  // a few sensible defaults. In V2 the type has no functional meaning
  // — the user can invent any string ("Rechnung", "Angebot",
  // "Vertrag", …). Mirrors the pattern in saas-pdf-api-frontend.
  const [knownTypes, setKnownTypes] = useState<string[]>([])

  // Keep handleLoadTemplates identity-stable. tRPC's useUtils +
  // useMutation return fresh references each render; without this ref
  // the callback identity would change every render, retrigger
  // TemplateListPage's load-effect, and infinite-loop.
  const clientRef = useRef(templateClient)
  clientRef.current = templateClient

  const handleLoadTemplates = useCallback(async (): Promise<
    { success: true; templates: Template[] } | { success: false; error: string }
  > => {
    const result = await clientRef.current.loadProjectList()
    if (result.success) {
      const types = Array.from(
        new Set(result.projects.map((p) => p.documentType).filter((v): v is string => !!v))
      ).sort()
      // Bail out when the set hasn't changed so we don't churn the
      // consumer's state.
      setKnownTypes((prev) =>
        prev.length === types.length && prev.every((v, i) => v === types[i]) ? prev : types
      )
      return {
        success: true,
        templates: result.projects.map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          type: p.documentType,
          // Without `isSystem` the list offers Edit/Delete on system templates,
          // which the server rejects (Issue API 2026-10-20).
          isSystem: p.isSystem,
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
          editorVersion: p.editorVersion,
        })),
      }
    }
    return result
  }, [])

  const handleDeleteTemplate = useCallback(
    async (id: string): Promise<{ success: true } | { success: false; error: string }> => {
      return templateClient.deleteTemplate(id)
    },
    [templateClient]
  )

  const handleDuplicateTemplate = useCallback(
    async (
      id: string
    ): Promise<{ success: true; newId: string } | { success: false; error: string }> => {
      return templateClient.duplicateTemplate(id)
    },
    [templateClient]
  )

  // The only create path (the legacy pdfTemplateClient.createTemplate is
  // gone, Issue API 2026-10-14): create directly through tRPC so the new row already lands in
  // the DB with a v2-shaped BlockTemplate + editorVersion: 'v2'. The
  // editor then mounts on this seeded template; the user's first save
  // simply overwrites with real content.
  const handleCreateTemplate = useCallback(
    async (input: {
      name: string
      description?: string
      type: string
    }): Promise<{ success: true; id: string } | { success: false; error: string }> => {
      try {
        // Seed both fields:
        //   • DB column `documentType` (used by list + badge filter)
        //   • JSONB `templateType` (read by the editor's Typ field —
        //     a free-form combobox since pdf-editor-frontend 1.94)
        // Without the JSONB mirror the editor would fall back to its
        // catalog default ("Sonstiges") for any host-app type that
        // isn't in TEMPLATE_TYPE_OPTIONS — silently hiding the value
        // the user just picked in the create dialog.
        // Papierformat, Ränder und Einheit aus dem Maßsystem des Betriebs (`starterTemplate`;
        // Issue API 2026-10-14 ia-fe-neue-vorlage-ignoriert-das-masssystem-des-betriebs).
        const seed = {
          ...starterTemplate(input.name, measurementSystem),
          templateType: input.type,
        } satisfies BlockTemplate & { templateType: string }
        const result = await createMutation.mutateAsync({
          name: input.name,
          description: input.description,
          documentType: input.type,
          template: seed as unknown as Record<string, unknown>,
          isDefault: false,
        })
        await trpcUtils.pdf.templateList.invalidate()
        return { success: true, id: result.id }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to create template',
        }
      }
    },
    [createMutation, trpcUtils, measurementSystem]
  )

  const translate = useCallback((key: string, fallback?: string) => t(key, fallback ?? key), [t])

  // Suggestions for the free-form type combobox: seed with the
  // app's primary case ('invoice') plus every type already in use
  // in the org. Labels equal values — no i18n round-trip — so the
  // user sees exactly what gets saved.
  const documentTypes = useMemo(() => {
    const merged = Array.from(new Set(['invoice', ...knownTypes]))
    return merged.map((value) => ({ value, label: value }))
  }, [knownTypes])

  return (
    <ConsentGuard>
      <TemplateListPage
        key={location.key}
        basePath="/pdf/v2"
        onLoadTemplates={handleLoadTemplates}
        onDeleteTemplate={handleDeleteTemplate}
        onDuplicateTemplate={handleDuplicateTemplate}
        onCreateTemplate={handleCreateTemplate}
        documentTypes={documentTypes}
        allowCustomType
        // V2 list: badges yes, but every row clickable — the new
        // editor opens v1 with a warn-modal and promotes on save.
        showEditorVersionBadge
        t={translate}
      />
    </ConsentGuard>
  )
}

/**
 * Log Audit Page - shows audit logs with filtering and export
 */
export function LogAuditPageWithProviders() {
  const { t, i18n } = useTranslation('logAudit')
  const locale = (i18n.language === 'de' ? 'de' : 'en') as 'de' | 'en'
  return (
    <ConsentGuard>
      <LogAuditPage t={t} locale={locale} showApiKeyColumn />
    </ConsentGuard>
  )
}

/**
 * Log Invoice Page - shows invoice logs with filtering and export
 */
export function LogInvoicePageWithProviders() {
  const { t, i18n } = useTranslation('logInvoice')
  const locale = (i18n.language === 'de' ? 'de' : 'en') as 'de' | 'en'
  return (
    <ConsentGuard>
      <LogInvoicePage t={t} locale={locale} />
    </ConsentGuard>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Settings Pages
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Settings Account Page - GDPR-compliant account management
 *
 * Oben das eigene Profil mit „E-Mail ändern" (Plan ALLGEMEIN 2026-09-22/03,
 * ENTSCHEID observer 2930 = b): bisher hing der Griff nur auf der Fremdroute
 * `/users/:userId/edit`, die bleibt. `memberId` und `currentUserId` sind beide
 * die eigene Id, also spricht die Karte „Anmeldemethoden" in der
 * Eigenkonto-Sicht. Ohne Anmeldung kein Formular: an eine leere Id gebunden
 * bliebe es im Ladezustand. „Abbrechen" setzt das Formular über `key` zurück.
 */
export function SettingsAccountPageWithProviders() {
  const { t } = useTranslation('user')
  const { user } = useAuth()
  const apiClient = useUserClient()
  const [formularStand, setFormularStand] = useState(0)
  return (
    <SettingsLayout>
      {user?.id ? (
        <UserProfileForm
          key={formularStand}
          apiClient={apiClient}
          memberId={user.id}
          currentUserId={user.id}
          t={t}
          onCancel={() => setFormularStand((n) => n + 1)}
          onSaved={() => {}}
        />
      ) : null}
      {/* Die aktiven Sitzungen stehen unter „Sicherheit" (neben 2FA). */}
      <AccountSettings showActiveSessions={false} />
    </SettingsLayout>
  )
}

export function SettingsSystemPageWithProviders() {
  return <SettingsSystemPage />
}

export function ChangelogPageWithProviders() {
  return <ChangelogPage />
}

export function CookieSettingsPageWithProviders() {
  return <CookieSettingsPage />
}

export function LegalSettingsPageWithProviders() {
  return <LegalSettingsPage />
}

export function SettingsEmailPageWithProviders() {
  return <SettingsEmailPage />
}

/** Geplante Berichte und Alarme (Plan CORE 2026-08-20/04, Schritt 5). */
export function SettingsNotificationsPageWithProviders() {
  return <SettingsNotificationsPage />
}

// ═══════════════════════════════════════════════════════════════════════════
// Sicherheit (Zwei-Faktor-Anmeldung)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sicherheits-Tab: 2FA einschalten, abschalten, Sicherungscodes neu erzeugen.
 *
 * Die Oberflaeche liegt geteilt in `core-frontend` `plugin-security`; hier
 * bekommt sie den TYPISIERTEN tRPC-Client dieser App und die Toast-Konvention
 * (Fehler bleiben stehen, Erfolg verschwindet — UI_GUIDELINES).
 *
 * 🔴 Der Tab war bis 2026-08-29 ABSICHTLICH nicht erreichbar: er haette Nutzer
 * ausgesperrt (zwei 2FA-Zweige teilten sich ein Flag, better-auths Tabelle
 * fehlte, die Anmeldemaske kannte die Rueckfrage nicht). Alle drei Ursachen
 * sind gemessen behoben — Begruendung an der Registrierung in
 * `app.plugins.tsx`.
 */
export function SecuritySettingsPageWithProviders() {
  const { t } = useTranslation('security')
  const client = useTwoFactorClient()

  // Eigenes Layout: die Plugin-Route kam bis 2026-09-25 ohne Einstellungs-
  // Navigation (wer „Sicherheit" oeffnete, verlor die Reiter). Die aktiven
  // Sitzungen stehen hier, nicht unter „Konto": „Wo bin ich angemeldet?" ist
  // dieselbe Frage wie die nach dem zweiten Faktor.
  return (
    <SettingsLayout>
      <div className="space-y-6">
        <SecuritySettingsTab
          client={client}
          t={(key, fallback) => t(key, { defaultValue: fallback ?? key })}
          onError={(message) => toast.error(message)}
          onSuccess={(message) => toast.success(message)}
        />
        <ActiveSessionsSection />
      </div>
    </SettingsLayout>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// E-Mail-Protokoll
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Protokoll der versendeten System-Mails (Einladung, Passwort-Reset, Billing).
 *
 * Der Router liegt seit Langem im Kernel und wurde von keinem Frontend
 * gelesen — „Ich habe keine Einladung bekommen" endete deshalb bei einem
 * Entwickler mit DB-Zugang. Der Tab beantwortet die Frage im Cockpit.
 *
 * 🔴 Fehler bleiben stehen (`toast.error`, persistent) — UI_GUIDELINES: ein
 * Protokoll, das seinen Ladefehler wegblendet, sieht aus wie ein leeres
 * Protokoll.
 */
export function EmailLogSettingsPageWithProviders() {
  const { t } = useTranslation('emailLog')
  const client = useEmailLogClient()

  // System-Seite: was die Organisation verschickt hat, ist kein persoenlicher
  // Wert. Bis 2026-09-25 ohne Layout — die Reiter verschwanden auf der Seite.
  return (
    <SettingsLayout section="system">
      <EmailLogTab
        client={client}
        t={(key: string, fallback?: string) => t(key, fallback ?? key)}
        onError={(message: string) => toast.error(message)}
      />
    </SettingsLayout>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// E-Mail-Versand (Schalter je Versandkategorie)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Je Versandkategorie: sofort senden oder zurückhalten (Issue API 2026-10-02 `ia-fe-ohne-postausgang-seite`).
 *
 * Inhalt und Texte bringt das Kernel-Plugin `email-outbox` mit; hier steht nur der Rahmen. Ohne ihn hätte die Seite
 * die Reiter nicht (Befund am E-Mail-Protokoll bis 2026-09-25). `section="system"`: der Schalter gilt für die ganze
 * Organisation und verlangt `organization:settings:update` — er gehört zu „System", nicht zu „Mein Konto".
 */
export function EmailDispatchSettingsPageWithProviders() {
  return (
    <SettingsLayout section="system">
      <EmailDispatchTab />
    </SettingsLayout>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Gov Credentials
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Government Credentials Settings Page with providers.
 *
 * countryMode="all": API customers submit invoices for arbitrary countries,
 * so every gov system from the catalog is offered — not just the org's own
 * country (which the API app does not track anyway).
 */
// 🔴 Die App-seitige Verdrahtung des Sicherheits-Tabs ist am 2026-08-23
// ENTFERNT worden — nicht weil sie falsch war, sondern weil sie toter Code
// gewesen waere: Der Tab ist absichtlich nicht registriert (er wuerde Nutzer
// aussperren, siehe `app.plugins.tsx`), und ihre Typen haengen an einer
// Router-Form, die erst mit dem naechsten Backend-Release veroeffentlicht
// wird. Beides zusammen heisst: sie kann nichts tun und bricht den Build.
//
// Der Tab selbst lebt fertig und getestet in core-frontend
// (`plugin-security`); die Wiederverdrahtung steht in
// `BLOCKED-DEFERRED/api_03-zwei-faktor-zwei-implementierungen.md`, zusammen
// mit dem Client-Code aus dieser Datei (git: f706c23).

export function GovCredentialsSettingsPageWithProviders() {
  const { t } = useTranslation('govCredentials')
  const apiClient = useGovCredentialsClient()
  const orgPassword = useOrgPassword()
  const [credentials, setCredentials] = useState<CredentialListItem[]>([])
  const [systems, setSystems] = useState<GovSystemListItem[]>([])
  const [isPasswordSetUp, setIsPasswordSetUp] = useState(false)

  const loadCredentials = useCallback(() => {
    apiClient
      .list()
      .then(setCredentials)
      .catch(() => {
        // credentials not available yet
      })
    apiClient
      .listSystems()
      .then(setSystems)
      .catch(() => {
        // systems catalog not available yet
      })
  }, [apiClient])

  const checkPasswordSetup = useCallback(() => {
    apiClient
      .getOrgPassword()
      .then((result) => setIsPasswordSetUp(result !== null))
      .catch(() => setIsPasswordSetUp(false))
  }, [apiClient])

  useEffect(() => {
    loadCredentials()
    checkPasswordSetup()
  }, [loadCredentials, checkPasswordSetup])

  return (
    <GovCredentialsSettingsPage
      t={t}
      apiClient={apiClient}
      credentials={credentials}
      systems={systems}
      countryMode="all"
      isVaultUnlocked={orgPassword.isUnlocked}
      vaultTimeRemaining={orgPassword.timeRemaining}
      isPasswordSetUp={isPasswordSetUp}
      onUnlockVault={(password: string) => orgPassword.unlock(password)}
      onLockVault={() => orgPassword.lock()}
      onPasswordSetUp={() => {
        setIsPasswordSetUp(true)
      }}
      onCredentialsChanged={() => {
        loadCredentials()
      }}
      getPassword={() => orgPassword.getPassword()}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// VeriFactu (ES)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ISO country of the active organisation, or `undefined` while it loads.
 *
 * Shared with `VerifactuNavGate`, which hides the sidebar entry for every
 * non-spanish organisation — same query, so React Query serves it from cache.
 */
export function useOrgCountry(): string | undefined {
  return useOrgVerifactuIdentity().countryCode
}

/**
 * Land **und** NIF der aktiven Organisation — beides aus derselben Abfrage.
 *
 * 🔴 Der NIF ist nicht Beiwerk: `VerifactuSettingsPage` blendet die ganze
 * Ketten-Flaeche (Zustand der Kette **und** den Knopf „Kette exportieren") aus,
 * solange `vatId` fehlt — der Kettenschluessel IST der NIF, ohne ihn gaebe es
 * keine Kette, die man adressieren koennte. Diese App hat ihn bis hierher nie
 * durchgereicht; die Flaeche war deshalb unsichtbar, ohne dass irgendetwas rot
 * wurde.
 */
export function useOrgVerifactuIdentity(): {
  countryCode: string | undefined
  vatId: string | undefined
  nif: string | undefined
  nombreRazon: string | undefined
} {
  const orgQuery = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  })
  return {
    countryCode: orgQuery.data?.countryCode ?? undefined,
    vatId: orgQuery.data?.vatId ?? undefined,
    // 🔴 `taxNumber`, NICHT der `vatId` ohne Praefix. Die AEAT-`Cabecera` will
    // `B12345678`, der `vatId` traegt bei spanischen Betrieben `ESB12345678`
    // (`ConsultaLR.xsd`). Das Praefix hier abzuschneiden waere eine Herleitung
    // im Browser fuer einen Wert, den der Server fuehrt — verboten nach „Was
    // der Server weiss, schickt ihm der Browser nicht zurueck". Also wird das
    // Feld genommen, das den NIF ohne Praefix FUEHRT: die Steuernummer des
    // Organisationsdatensatzes. Fehlt sie, bleibt der Wert `undefined` und die
    // Abgleich-Flaeche sagt das ausdruecklich, statt einen NIF zu erfinden.
    nif: orgQuery.data?.taxNumber ?? undefined,
    // `legalName` ist die Firmierung, `name` der Anzeigename — die AEAT will
    // die Firmierung, deshalb diese Reihenfolge. Beides kommt vom Server.
    nombreRazon: orgQuery.data?.legalName ?? orgQuery.data?.name ?? undefined,
  }
}

/**
 * VeriFactu-Seite (`/settings/verifactu`) — nur für spanische Organisationen
 * inhaltlich befüllt.
 *
 * Diese App lädt `plugin-invoice` nicht, hat also keine Invoice-Settings-Seite
 * als Trägerseite für die ES-Kachel. Die Seite hängt deshalb als eigener
 * Sidebar-Eintrag unter „Einstellungen" — Art. 13.2 RD 1007/2023 verlangt, dass
 * die declaración responsable *im System selbst* schnell und intuitiv einsehbar
 * ist, und dieses Backend erzeugt spanische Registros.
 *
 * Zeigt die declaración responsable, den Aktivierungs-Schalter pro Organisation
 * (Art. 2.c Orden HAC/1177/2024, Jahresbindung Art. 16.5) und die Registros,
 * die es nicht zur AEAT geschafft haben, inkl. manuellem Retry.
 */
export function VerifactuSettingsPageWithProviders() {
  const { t } = useTranslation('verifactu')
  const apiClient = useVerifactuClient()
  const { countryCode, vatId, nif, nombreRazon } = useOrgVerifactuIdentity()
  return (
    <ConsentGuard>
      <VerifactuSettingsPage
        apiClient={apiClient}
        t={t}
        countryCode={countryCode}
        vatId={vatId}
        nif={nif}
        nombreRazon={nombreRazon}
      />
    </ConsentGuard>
  )
}
